/**
 * Mobile PDP: Add to Cart stays in document flow until scrolled out of view,
 * then pins to the bottom of the viewport.
 *
 * Why the button needs to be portaled to <body> instead of just `position:fixed`:
 *   `.product` on mobile is `position: relative; z-index: 4; overflow: hidden`
 *   (see _sm-style-1.css). That creates a stacking context AND a clipping
 *   context. A `position: fixed` descendant of `.product` is:
 *     a) trapped inside .product's stacking context (it cannot paint above
 *        sibling sections like <dual-video-player>), and
 *     b) subject to an iOS Safari bug where fixed descendants of an
 *        overflow:hidden ancestor get clipped / stop painting once that
 *        ancestor has scrolled out of view -- matches the "slides behind
 *        the video section and doesn't come back" symptom reported on device.
 *
 *   Moving the button to document.body while sticky sidesteps both problems:
 *   the button is no longer inside .product, so it escapes the stacking
 *   context and the overflow clip entirely.
 *
 * Other details:
 *   - Sticky when the in-flow placeholder has scrolled above the viewport
 *     (bottom <= 0). Not when it is still below the fold (top > innerHeight).
 *   - Scroll/resize listeners re-check on each frame; IntersectionObserver alone
 *     stops firing once non-intersecting, even while the block is still visible.
 *   - The original container keeps a min-height placeholder so content
 *     above/below the form doesn't shift when we portal the button out.
 */
(function () {
  var MQ = '(max-width: 480px)';
  var SEL_CONTAINER = '.add-to-cart.js-pdp-add-to-cart-sticky';
  var SEL_BUTTON = '.btn--secondary';
  var PORTAL_CLASS = 'js-pdp-add-to-cart-portal';
  var FORM_ID = 'add-to-cart';
  var CART_DRAWER_ID = 'CartSidebar';
  var CART_ACTIVE_CLASS = 'active';

  function init() {
    var container = document.querySelector(SEL_CONTAINER);
    if (!container) return;

    var button = container.querySelector(SEL_BUTTON);
    if (!button) return;

    var portal = document.createElement('div');
    portal.className = PORTAL_CLASS;
    document.body.appendChild(portal);

    var originalButtonNextSibling = button.nextSibling;
    var originalButtonParent = button.parentNode;

    if (!button.getAttribute('form')) {
      button.setAttribute('form', FORM_ID);
    }

    var mq = window.matchMedia(MQ);
    var resizeTimer;
    var portaled = false;
    var scrollTicking = false;
    var bound = false;

    function measureButtonHeight() {
      return button.offsetHeight;
    }

    function setPlaceholderHeight() {
      container.style.setProperty(
        '--add-to-cart-sticky-h',
        measureButtonHeight() + 'px'
      );
    }

    function portalButton() {
      if (portaled) return;
      setPlaceholderHeight();
      container.classList.add('is-sticky-active');
      portal.appendChild(button);
      portal.classList.add('is-active');
      portaled = true;
    }

    function unportalButton() {
      container.classList.remove('is-sticky-active');
      container.style.removeProperty('--add-to-cart-sticky-h');
      if (!portaled) return;
      if (
        originalButtonNextSibling &&
        originalButtonNextSibling.parentNode === originalButtonParent
      ) {
        originalButtonParent.insertBefore(button, originalButtonNextSibling);
      } else {
        originalButtonParent.appendChild(button);
      }
      portal.classList.remove('is-active');
      portaled = false;
    }

    function setSticky(on) {
      if (on) portalButton();
      else unportalButton();
    }

    function shouldBeSticky() {
      var rect = container.getBoundingClientRect();
      var viewH = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top > viewH) return false;
      return rect.bottom <= 0;
    }

    function syncSticky() {
      if (!mq.matches) return;
      setSticky(shouldBeSticky());
    }

    function onScrollOrResize() {
      if (!mq.matches || scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(function () {
        scrollTicking = false;
        syncSticky();
      });
    }

    function bind() {
      if (bound) {
        window.removeEventListener('scroll', onScrollOrResize);
        window.removeEventListener('resize', onScrollOrResize);
        bound = false;
      }
      setSticky(false);
      if (!mq.matches) return;
      bound = true;
      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize, { passive: true });
      syncSticky();
    }

    bind();
    if (mq.addEventListener) {
      mq.addEventListener('change', bind);
    } else {
      mq.addListener(bind);
    }

    var cartDrawer = document.getElementById(CART_DRAWER_ID);
    function syncCartDrawerState() {
      if (!cartDrawer) return;
      var open = cartDrawer.classList.contains(CART_ACTIVE_CLASS);
      portal.classList.toggle('is-cart-open', open);
    }
    if (cartDrawer && typeof MutationObserver !== 'undefined') {
      syncCartDrawerState();
      new MutationObserver(syncCartDrawerState).observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    window.addEventListener(
      'resize',
      function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          if (mq.matches && portaled) {
            setPlaceholderHeight();
          }
        }, 150);
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
